import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Modal } from "react-bootstrap";
import {
  FaRocket,
  FaBullseye,
  FaChartPie,
  FaUsers,
  FaTrophy,
  FaChartLine,
  FaCheckCircle,
  FaFileAlt,
  FaArrowUp,
  FaTimes,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { base_url } from "../../utils/axiosConfig";

// ===================== VALIDATION SCHEMA =====================
const validationSchema = Yup.object({
  name: Yup.string().required("Full name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, "Phone must be 10 digits")
    .required("Phone number is required"),
  address_line: Yup.string().required("Address is required"),
  city: Yup.string().required("City is required"),
  district: Yup.string().required("District is required"),
  state: Yup.string().required("State is required"),
  country: Yup.string().required("Country is required"),
  pincode: Yup.string()
    .matches(/^[0-9]{6}$/, "Pincode must be 6 digits")
    .required("Pincode is required"),
  businessName: Yup.string().nullable(),
  gstNo: Yup.string().nullable(),
});

const steps = [
  { title: "Company Overview", icon: <FaBullseye size={14} /> },
  { title: "Updates & Materials", icon: <FaTrophy size={14} /> },
  { title: "Register Interest", icon: <FaRocket size={14} /> },
];

const InvestorRelationsModal = ({ show, onHide }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const initialValues = {
    name: "",
    email: "",
    phone: "",
    address_line: "",
    city: "",
    district: "",
    state: "",
    country: "",
    pincode: "",
    businessName: "",
    gstNo: "",
  };

  const handleNext = () => setCurrentStep((prev) => prev + 1);
  const handlePrev = () => setCurrentStep((prev) => prev - 1);

  const handleClose = () => {
    setCurrentStep(0);
    setSubmitted(false);
    onHide();
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setLoading(true);
    try {
      const payload = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        address: {
          address_line: values.address_line,
          city: values.city,
          district: values.district,
          state: values.state,
          country: values.country,
          pincode: values.pincode,
        },
        businessName: values.businessName || null,
        gstNo: values.gstNo || null,
      };

      await axios.post( `${base_url}v1/user/signupInvestor`, payload);
      setSubmitted(true);
      resetForm();
      toast.success(
        "Registration successful! Our team will contact you shortly.",
      );
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      dialogClassName="modal-90w md:modal-lg"
    >
      <Modal.Body className="p-4 md:p-6">
        <div className="relative bg-white rounded-lg">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 bg-transparent border-0"
          >
            <FaTimes size={16} color="#666" />
          </button>

          {/* Header */}
          {!submitted && (
            <div className="text-center mb-4">
              <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#00C70D] to-[#005AD0] bg-clip-text text-transparent">
                Investor Relations
              </h3>
              <p className="text-gray-500 text-xs md:text-sm">
                Join LifeOnPlus in revolutionizing preventive & digital
                healthcare
              </p>
            </div>
          )}

          {/* Success State */}
          {submitted ? (
            <div className="text-center py-6">
              <FaCheckCircle className="mx-auto text-green-600" size={48} />
              <h2 className="text-lg md:text-xl font-bold text-green-600 mt-3">
                Registration Successful
              </h2>
              <p className="text-gray-500 text-sm mt-2">
                Your interest has been registered successfully. Our Investor
                Relations team will contact you shortly.
              </p>
              <button
                onClick={handleClose}
                className="mt-4 px-6 py-2 bg-[#00C70D] text-white font-semibold text-sm rounded-md shadow-md hover:opacity-90"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Steps Indicator */}
              <div className="flex flex-wrap gap-2 mb-5 justify-center">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium ${
                      index === currentStep
                        ? "bg-[#00C70D] text-white"
                        : index < currentStep
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {step.icon}
                    <span>{step.title}</span>
                  </div>
                ))}
              </div>

              {/* ===================== STEP 1: Company Overview ===================== */}
              {currentStep === 0 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                      <h4 className="text-sm font-semibold flex items-center gap-2 mb-2">
                        <FaBullseye className="text-[#00C70D]" /> Vision
                      </h4>
                      <p className="text-xs text-gray-600">
                        To be a global leader in preventive, digital, and
                        democratized healthcare— empowering every individual to
                        live healthier, longer, and fuller lives.
                      </p>
                    </div>
                    <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                      <h4 className="text-sm font-semibold flex items-center gap-2 mb-2">
                        <FaRocket className="text-[#00C70D]" /> Mission
                      </h4>
                      <p className="text-xs text-gray-600">
                        To make healthcare more accessible, personalized, and
                        non-invasive through smart technologies, strategic
                        partnerships, and scalable wellness ecosystems.
                      </p>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                    <h4 className="text-sm font-semibold flex items-center gap-2 mb-2">
                      <FaChartPie className="text-[#00C70D]" /> Market
                      Opportunity
                    </h4>
                    <ul className="text-xs text-gray-600 list-disc ml-4 space-y-1">
                      <li>
                        70% of global deaths are caused by Non-Communicable
                        Diseases (NCDs)
                      </li>
                      <li>
                        Huge gap in affordable, accessible and preventive
                        healthcare
                      </li>
                      <li>
                        Strong government push for Digital Health Mission in
                        India
                      </li>
                      <li>
                        Rising demand for non-invasive diagnostics and digital
                        health records
                      </li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                    <h4 className="text-sm font-semibold flex items-center gap-2 mb-2">
                      <FaChartLine className="text-[#00C70D]" /> Business Model
                      & Revenue Streams
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-600">
                      <div>
                        <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium mr-1">
                          B2C
                        </span>{" "}
                        App Subscriptions & Digital Health Cards
                      </div>
                      <div>
                        <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium mr-1">
                          B2B
                        </span>{" "}
                        Corporate Wellness Programs
                      </div>
                      <div>
                        <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px] font-medium mr-1">
                          Franchise
                        </span>{" "}
                        Body Recharge Stations
                      </div>
                      <div>
                        <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-[10px] font-medium mr-1">
                          Channel
                        </span>{" "}
                        Distributor Network
                      </div>
                      <div>
                        <span className="inline-block px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded text-[10px] font-medium mr-1">
                          Marketplace
                        </span>{" "}
                        Diagnostics & Devices
                      </div>
                      <div>
                        <span className="inline-block px-2 py-0.5 bg-pink-100 text-pink-700 rounded text-[10px] font-medium mr-1">
                          White Label
                        </span>{" "}
                        Healthcare Collaborations
                      </div>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                    <h4 className="text-sm font-semibold flex items-center gap-2 mb-2">
                      <FaUsers className="text-[#00C70D]" /> Founder &
                      Leadership
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="font-semibold">
                          Mr. Rajanikant M. Torgal
                        </p>
                        <p className="text-gray-500">Founder & CEO</p>
                        <p className="text-gray-600 mt-1">
                          Visionary entrepreneur with 30+ years experience in
                          telecommunications, AI and health technology.
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold">Dr. S. Arulrhaj</p>
                        <p className="text-gray-500">Chairman</p>
                        <p className="text-gray-600 mt-1">
                          Eminent physician with 42+ years experience. President
                          of Commonwealth Medical Association.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                    <h4 className="text-sm font-semibold flex items-center gap-2 mb-2">
                      <FaTrophy className="text-[#00C70D]" /> Growth Milestones
                    </h4>
                    <div className="space-y-2 text-xs">
                      {[
                        {
                          year: "2011",
                          desc: "Conceptualized after personal healthcare challenge in South Korea",
                        },
                        {
                          year: "2016",
                          desc: "Foundation through Paxykop Technology, Singapore",
                        },
                        {
                          year: "2020",
                          desc: "Resumed development of non-invasive technologies",
                        },
                        {
                          year: "2023",
                          desc: "Launch of LifeOnPlus App & pilot projects in India",
                        },
                        {
                          year: "2024",
                          desc: "Body Recharge Station franchise & Smart Health Card rollout",
                        },
                        {
                          year: "2025 & Beyond",
                          desc: "National expansion, AI diagnostics, global partnerships & IPO preparation",
                        },
                      ].map((item, idx) => (
                        <div key={idx} className="flex gap-3">
                          <span className="font-semibold text-[#00C70D] min-w-[90px]">
                            {item.year}
                          </span>
                          <span className="text-gray-600">{item.desc}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-2 border-[#00C70D] rounded-md p-3 bg-green-50">
                    <h4 className="text-sm font-semibold flex items-center gap-2 mb-1">
                      <FaRocket className="text-[#00C70D]" /> Investment
                      Proposal
                    </h4>
                    <p className="text-xs text-gray-700">
                      We invite strategic investors and partners who share our
                      vision of democratizing preventive healthcare. Join us in
                      building one of India’s most impactful digital health
                      platforms.
                    </p>
                  </div>
                </div>
              )}

              {/* ===================== STEP 2: Updates & Materials ===================== */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                    <h4 className="text-sm font-semibold flex items-center gap-2 mb-3">
                      <FaArrowUp className="text-[#00C70D]" /> Latest Updates
                    </h4>

                    <h5 className="text-xs font-semibold mb-1">
                      New Partnerships
                    </h5>
                    <p className="text-xs text-gray-600 mb-3">
                      Strategic collaborations with hospitals, diagnostic chains
                      and corporate wellness providers across India.
                    </p>

                    <h5 className="text-xs font-semibold mb-1">
                      Customer Growth
                    </h5>
                    <p className="text-xs text-gray-600 mb-3">
                      Rapidly growing user base through app downloads, Digital
                      Health Smart Cards and franchise network expansion.
                    </p>

                    <h5 className="text-xs font-semibold mb-1">
                      Product Launches
                    </h5>
                    <ul className="text-xs text-gray-600 list-disc ml-4 mb-3 space-y-0.5">
                      <li>Global Digital Health Smart Card</li>
                      <li>Body Recharge Stations</li>
                      <li>AI-powered health insights</li>
                      <li>LifeOnPlus Mobile App (Android & iOS)</li>
                    </ul>

                    <h5 className="text-xs font-semibold mb-1">
                      Awards & Certifications
                    </h5>
                    <p className="text-xs text-gray-600 mb-3">
                      Recognition for innovation in digital healthcare and
                      preventive wellness technologies.
                    </p>

                    <h5 className="text-xs font-semibold mb-1">
                      Success Stories
                    </h5>
                    <p className="text-xs text-gray-600">
                      Early detection of health issues, improved employee
                      wellness programs, and successful franchise partners
                      across multiple cities.
                    </p>
                  </div>

                  <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                    <h4 className="text-sm font-semibold flex items-center gap-2 mb-3">
                      <FaFileAlt className="text-[#00C70D]" /> Professional
                      Investor Materials
                    </h4>
                    <div className="space-y-3">
                      {[
                        {
                          title: "Investor Presentation (Pitch Deck)",
                          desc: "Complete overview of business, market & opportunity",
                        },
                        {
                          title: "Business Plan",
                          desc: "Detailed strategy, operations and growth roadmap",
                        },
                        {
                          title: "Financial Projections",
                          desc: "Revenue forecasts, unit economics and key metrics",
                        },
                      ].map((item, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between items-start border-b border-gray-200 pb-2 last:border-0"
                        >
                          <div>
                            <p className="text-xs font-semibold">
                              {item.title}
                            </p>
                            <p className="text-[11px] text-gray-500">
                              {item.desc}
                            </p>
                          </div>
                          <button className="text-[#005AD0] text-xs font-medium hover:underline">
                            Request Access
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ===================== STEP 3: Registration Form ===================== */}
              {/* ===================== STEP 3: Registration Form ===================== */}
              {currentStep === 2 && (
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ isSubmitting }) => (
                    <Form className="flex flex-col gap-2">
                      {/* Personal Details - 3 in 1 row */}
                      <h4 className="text-sm font-semibold">
                        Personal Details
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="name"
                            placeholder="Enter Full Name"
                            className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                          />
                          <ErrorMessage
                            name="name"
                            component="div"
                            className="text-red-500 text-[10px] mt-0.5"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1">
                            Email Address{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="email"
                            name="email"
                            placeholder="Enter Email"
                            className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                          />
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="text-red-500 text-[10px] mt-0.5"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1">
                            Phone Number <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="phone"
                            placeholder="10-digit mobile number"
                            className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                          />
                          <ErrorMessage
                            name="phone"
                            component="div"
                            className="text-red-500 text-[10px] mt-0.5"
                          />
                        </div>
                      </div>

                      <hr className="border-gray-200" />

                      {/* Address Details - 6 inputs → 3 per row */}
                      <h4 className="text-sm font-semibold mb-1">
                        Address Details
                      </h4>

                      {/* Row 1: Address Line + City + District */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">
                            Address Line <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="address_line"
                            placeholder="House No, Street, Area"
                            className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                          />
                          <ErrorMessage
                            name="address_line"
                            component="div"
                            className="text-red-500 text-[10px] mt-0.5"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1">
                            City <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="city"
                            placeholder="City"
                            className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                          />
                          <ErrorMessage
                            name="city"
                            component="div"
                            className="text-red-500 text-[10px] mt-0.5"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1">
                            District <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="district"
                            placeholder="District"
                            className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                          />
                          <ErrorMessage
                            name="district"
                            component="div"
                            className="text-red-500 text-[10px] mt-0.5"
                          />
                        </div>
                      </div>

                      {/* Row 2: State + Pincode + Country */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">
                            State <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="state"
                            placeholder="State"
                            className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                          />
                          <ErrorMessage
                            name="state"
                            component="div"
                            className="text-red-500 text-[10px] mt-0.5"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1">
                            Pincode <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="pincode"
                            placeholder="6-digit pincode"
                            className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                          />
                          <ErrorMessage
                            name="pincode"
                            component="div"
                            className="text-red-500 text-[10px] mt-0.5"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1">
                            Country <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="country"
                            placeholder="Country"
                            className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                          />
                        </div>
                      </div>

                      <hr className="my-2 border-gray-200" />

                      {/* Company Details - 2 in 1 row */}
                      <h4 className="text-sm font-semibold mb-1">
                        Company Details (Optional)
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">
                            Business / Company Name
                          </label>
                          <Field
                            type="text"
                            name="businessName"
                            placeholder="Optional"
                            className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1">
                            GST Number
                          </label>
                          <Field
                            type="text"
                            name="gstNo"
                            placeholder="Optional"
                            className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                          />
                        </div>
                      </div>

                      {/* Buttons */}
                      <div className="mt-6 flex justify-center gap-3">
                        <button
                          type="button"
                          className="w-28 md:w-36 py-2 bg-gray-300 text-gray-800 font-semibold text-sm md:text-base rounded-md shadow-md hover:bg-gray-400"
                          onClick={handlePrev}
                        >
                          Back
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting || loading}
                          className={`w-28 md:w-36 py-2 bg-[#00C70D] text-white font-semibold text-sm md:text-base rounded-md shadow-md flex items-center justify-center ${
                            isSubmitting || loading
                              ? "opacity-75 cursor-not-allowed"
                              : ""
                          }`}
                        >
                          {isSubmitting || loading ? (
                            <>
                              <svg
                                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Loading...
                            </>
                          ) : (
                            "Register"
                          )}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              )}

              {/* Navigation Buttons for Step 0 & 1 */}
              {currentStep < 2 && (
                <div className="mt-6 flex justify-center gap-3">
                  {currentStep > 0 && (
                    <button
                      type="button"
                      className="w-28 md:w-36 py-2 bg-gray-300 text-gray-800 font-semibold text-sm md:text-base rounded-md shadow-md hover:bg-gray-400"
                      onClick={handlePrev}
                    >
                      Back
                    </button>
                  )}
                  <button
                    type="button"
                    className="w-28 md:w-36 py-2 bg-[#00C70D] text-white font-semibold text-sm md:text-base rounded-md shadow-md hover:opacity-90"
                    onClick={handleNext}
                  >
                    Continue
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default InvestorRelationsModal;
